/**
 * Right-click / context-menu actions on a conversation (CDT-6).
 *
 *   const { togglePin, toggleMute, archive, markRead, deleteChat } =
 *     useConversationActions()
 *   togglePin(conv.id)
 *
 * Each action is **optimistic + rollback**: the store mutation runs first
 * (so the UI reflects the intent immediately), the API call follows, and
 * on REST failure the previous state is restored. Errors surface to the
 * console — no toast surface exists yet at this layer (a CDT for the
 * shared toast story); callers can pass `onError` to display their own.
 *
 * Wire/UI split: the store half is the source of truth. The legacy UI
 * `conversations` array in `PresencePage` is independently mutated by the
 * page so the mock unread-badge math still works during the wire/UI
 * coexistence window (W13 leftover).
 *
 * Backend semantics (per B2):
 *   • togglePin / toggleMute / archive → `PATCH /conversations/:id` updates
 *     the caller's `messenger.conversation_participants` row flag.
 *   • clearChat  → `POST /conversations/:id/clear`. Empties the thread FOR ME
 *     and keeps it in my list.
 *   • deleteChat → `DELETE /conversations/:id`. Empties the thread AND drops it
 *     from my list; a new message resurfaces it carrying only what arrived
 *     after the delete.
 *
 * Both are ONE-SIDED — the floor lives on my own participant row, so the other
 * party keeps their history and is never told. Both also purge IndexedDB: the
 * server agreeing the messages are gone is not enough on its own, because the
 * local-first cache would hydrate them straight back on the next reload.
 *   • markRead → `POST /conversations/:id/read` — monotonic + future-clamped
 *     server-side; safe to fire-and-forget.
 *
 * Coexists with `useMarkRead` (W10) — that hook auto-fires on scroll-to-tail
 * within an open thread. This hook's `markRead` is the *manual* "mark as
 * read" right-click affordance; both go through the same REST call so the
 * server semantics keep us honest.
 *
 *   • exitGroup: `DELETE /conversations/:id/members/:myUserId`, via
 *     `useGroupActions().removeMember`. Unlike deleteChat, this really does
 *     drop my participant row, so the group keeps going for everyone else
 *     and a "left" system message posts to the others.
 */

import { useCallback } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import {
  messengerApi,
  MessengerApiError,
} from '../services/messenger.api'
import type { Conversation as WireConversation } from '../services/messenger.wire'
import { useMessengerStore } from '../store/messenger.store'
import { purgeConversation, purgeConversationMessages } from '../services/messenger.db'
import { _forgetMessageSeed } from './useConversationMessages'
import { useGroupActions } from './useGroupActions'

interface UseConversationActions {
  togglePin:   (conversationId: string) => Promise<void>
  toggleMute:  (conversationId: string) => Promise<void>
  archive:     (conversationId: string) => Promise<void>
  unarchive:   (conversationId: string) => Promise<void>
  markRead:    (conversationId: string) => Promise<void>
  /** Empty the thread for me, keeping it in my list. */
  clearChat:   (conversationId: string, opts?: ClearOptions) => Promise<void>
  /** Empty the thread for me AND drop it from my list. */
  deleteChat:  (conversationId: string, opts?: ClearOptions) => Promise<void>
  /** Leave a group for real, dropping my participant row instead of just hiding the thread. */
  exitGroup:   (conversationId: string) => Promise<void>
}

/** WhatsApp's "Keep starred messages" checkbox. Defaults to keeping them. */
export interface ClearOptions {
  keepStarred?: boolean
}

function logErr(action: string, err: unknown): void {
  const msg = err instanceof MessengerApiError ? err.message : String(err)
   
  console.error(`[messenger] ${action} failed:`, msg)
}

export function useConversationActions(): UseConversationActions {
  // Pin/mute/archive/read flags live on MY participant row, which is keyed by
  // contactPresenceId (not the auth user id). All these actions target that row.
  const myUserId = useAuthStore((s) => s.presenceId ?? null)
  const { removeMember } = useGroupActions()

  const togglePin = useCallback(async (conversationId: string) => {
    if (!myUserId) return
    const state = useMessengerStore.getState()
    const conv = state.wireConversations.get(conversationId)
    if (!conv) return
    const me = conv.participants?.find((p) => p.userId === myUserId)
    const next = !me?.pinned

    state.patchWireParticipantFlags(conversationId, myUserId, { pinned: next })
    try {
      await messengerApi.patchConversation(conversationId, { pinned: next })
    } catch (err) {
      // Rollback to whatever was there pre-toggle.
      useMessengerStore.getState().patchWireParticipantFlags(
        conversationId,
        myUserId,
        { pinned: !next },
      )
      logErr('togglePin', err)
    }
  }, [myUserId])

  const toggleMute = useCallback(async (conversationId: string) => {
    if (!myUserId) return
    const state = useMessengerStore.getState()
    const conv = state.wireConversations.get(conversationId)
    if (!conv) return
    const me = conv.participants?.find((p) => p.userId === myUserId)
    const next = !me?.muted

    state.patchWireParticipantFlags(conversationId, myUserId, { muted: next })
    try {
      await messengerApi.patchConversation(conversationId, { muted: next })
    } catch (err) {
      useMessengerStore.getState().patchWireParticipantFlags(
        conversationId,
        myUserId,
        { muted: !next },
      )
      logErr('toggleMute', err)
    }
  }, [myUserId])

  const archive = useCallback(async (conversationId: string) => {
    if (!myUserId) return
    useMessengerStore.getState().patchWireParticipantFlags(
      conversationId,
      myUserId,
      { archived: true },
    )
    try {
      await messengerApi.patchConversation(conversationId, { archived: true })
    } catch (err) {
      useMessengerStore.getState().patchWireParticipantFlags(
        conversationId,
        myUserId,
        { archived: false },
      )
      logErr('archive', err)
    }
  }, [myUserId])

  const unarchive = useCallback(async (conversationId: string) => {
    if (!myUserId) return
    useMessengerStore.getState().patchWireParticipantFlags(
      conversationId,
      myUserId,
      { archived: false },
    )
    try {
      await messengerApi.patchConversation(conversationId, { archived: false })
    } catch (err) {
      useMessengerStore.getState().patchWireParticipantFlags(
        conversationId,
        myUserId,
        { archived: true },
      )
      logErr('unarchive', err)
    }
  }, [myUserId])

  const markRead = useCallback(async (conversationId: string) => {
    if (!myUserId) return
    // Optimistic: bump my participant's lastReadAt to now so the badge
    // clears immediately. Server clamps to its own clock — drift is OK.
    const nowIso = new Date().toISOString()
    const state = useMessengerStore.getState()
    state.applyReadUpdate(conversationId, myUserId, nowIso)
    try {
      await messengerApi.markRead(conversationId, { atIso: nowIso })
    } catch (err) {
      // No clean rollback path — the previous lastReadAt isn't retained on
      // a single mutation. Treat as best-effort; channel `read:updated`
      // events will reconcile shortly anyway.
      logErr('markRead', err)
    }
  }, [myUserId])

  const clearChat = useCallback(async (conversationId: string, opts: ClearOptions = {}) => {
    if (!myUserId) return
    const keepStarred = opts.keepStarred ?? true

    // Optimistic: empty the thread and the cache. Kept messages are NOT spared
    // locally — the client doesn't hold the authoritative star set — so any
    // survivors are re-seeded from the server below rather than guessed at.
    useMessengerStore.getState().clearWireMessages(conversationId)
    await purgeConversationMessages(conversationId)
    _forgetMessageSeed(conversationId)

    try {
      const { keptCount } = await messengerApi.clearConversation(conversationId, keepStarred)
      if (keptCount > 0) {
        // Bring the kept messages back from the source of truth rather than
        // making the user reopen the thread to see them.
        const page = await messengerApi.listMessages(conversationId, { limit: 50 })
        useMessengerStore.getState().seedWireMessages(conversationId, page.items, {
          nextCursor: page.nextCursor,
          hasMore: page.hasMore,
        })
      }
    } catch (err) {
      // The thread is empty locally but intact on the server. Forgetting the
      // seed means the next open re-hydrates it in full, so the failure costs a
      // reopen rather than any data.
      logErr('clearChat', err)
    }
  }, [myUserId])

  const deleteChat = useCallback(async (conversationId: string, opts: ClearOptions = {}) => {
    if (!myUserId) return
    const keepStarred = opts.keepStarred ?? true
    // Snapshot so we can restore on REST failure.
    const snapshot: WireConversation | undefined =
      useMessengerStore.getState().wireConversations.get(conversationId)
    if (!snapshot) return

    useMessengerStore.getState().removeWireConversation(conversationId)
    // `useConversationMessages` memoises its REST seed in a module-scoped
    // Set. Without clearing this entry, the conv reopens (e.g. after CDT-9
    // resurface) with an empty thread because the seed effect short-circuits.
    _forgetMessageSeed(conversationId)
    await purgeConversation(conversationId)
    try {
      await messengerApi.leaveConversation(conversationId, keepStarred)
    } catch (err) {
      // Restore the wire conversation row so the user can retry. Thread
      // messages stay dropped — they'll re-hydrate on next open via
      // `useConversationMessages` (the seed cache forgets after remove).
      useMessengerStore.getState().upsertWireConversation(snapshot)
      logErr('deleteChat', err)
    }
  }, [myUserId])

  const exitGroup = useCallback(async (conversationId: string) => {
    // Thrown rather than returned: the caller reports success on resolve, and
    // an unresolved presence id means nothing was left.
    if (!myUserId) throw new Error('Your account is still loading. Try again in a moment.')
    // No optimistic removal here, unlike deleteChat: exiting can fail for
    // reasons worth surfacing (already removed, server error), so the group
    // only leaves the list once the server has actually dropped the row.
    await removeMember(conversationId, myUserId)
    // Left, not deleted: the group stays in my list read-only, history and all,
    // until I choose Delete group (deleteChat). Dropping it here is what made
    // Exit group look like it deleted the whole group.
    useMessengerStore.getState().markRemovedFrom(conversationId)
    const conv = useMessengerStore.getState().wireConversations.get(conversationId)
    if (conv) useMessengerStore.getState().upsertWireConversation({ ...conv, left: true, leftAt: new Date().toISOString() })
  }, [myUserId, removeMember])

  return { togglePin, toggleMute, archive, unarchive, markRead, clearChat, deleteChat, exitGroup }
}
